/**
 * OpenClawSkills — Agent Skills Whitelist
 * 
 * Shows skills installed on the machine and lets the user enable/disable
 * each skill for the agent. Saves whitelist via API → openclaw.json sync.
 * 
 * Pattern: Same as Detalhes/Cérebro/Integrações (load → edit → save → toast)
 */
import { useState, useEffect, useCallback } from 'preact/hooks';
import { isAdmin } from '../stores/auth.store';
import { SkillBadge, ApiKeyIndicator } from './skills-center/SkillBadge';
import { useSkillsMeta, enrichSkill } from './skills-center/useSkillsMeta';
import { LockOverlay } from './LockOverlay';
import { SkeletonBar } from './ui';
import { T } from '../styles/tokens';
import { Button } from './ui';

interface Props {
  runnerId: string;
  isOnline: boolean;
  openclawPath?: string;
  cloudMachineId?: string;
  agentId?: string;
  locked?: boolean;
}

interface SkillItem {
  name: string;
  description: string;
  emoji: string;
  category: string;
  version: string;
  author: string;
  tags: string[];
  installed: boolean;
  enabled: boolean; // agent whitelist
}

type SaveResult = { type: 'success' | 'error'; message: string } | null;

interface AllSkillItem {
  id: string;
  name: string;
  description: string;
  category: string;
  version: string;
}

interface PendingInstall {
  id: string;
  skill_name: string;
  status: string;
  error_message?: string;
}

export function OpenClawSkills({ runnerId, isOnline, openclawPath, cloudMachineId, agentId = 'main', locked }: Props) {
  const [skills, setSkills] = useState<SkillItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saveResult, setSaveResult] = useState<SaveResult>(null);
  const [dirty, setDirty] = useState(false);
  const [syncInfo, setSyncInfo] = useState<{ source: string; time: string } | null>(null);

  // Download Skills panel state
  const [showDownload, setShowDownload] = useState(false);
  const [allSkills, setAllSkills] = useState<AllSkillItem[]>([]);
  const [loadingAll, setLoadingAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSkills, setSelectedSkills] = useState<Set<string>>(new Set());
  const [installing, setInstalling] = useState(false);
  const [pendingInstalls, setPendingInstalls] = useState<PendingInstall[]>([]);

  const { meta: skillsMeta } = useSkillsMeta();

  // Use runnerId for API calls — skills are stored under runner ID in DB
  // cloudMachineId is a different identifier and causes mismatches
  const machineId = runnerId;
  const installMachineId = cloudMachineId || runnerId;

  // Auto-dismiss toast
  useEffect(() => {
    if (!saveResult) return;
    const t = setTimeout(() => setSaveResult(null), 4000);
    return () => clearTimeout(t);
  }, [saveResult]);

  const loadSkills = useCallback(async () => {
    if (!isOnline) return;
    setLoading(true);
    setError('');
    try {
      const { api } = await import('../api/client.js');

      // 1. Get all available skills for this machine (installed status)
      const availRes = await api.get<{
        data?: {
          availableSkills: Array<{
            name: string; description: string; emoji: string;
            category: string; version: string; author: string;
            tags: string[]; installed: boolean;
          }>;
        };
        availableSkills?: Array<any>;
      }>(`/machines/${machineId}/available-skills`);

      const availData = (availRes as any)?.data || availRes;
      const availableSkills = availData.availableSkills || [];

      // 2. Get agent's currently enabled skills (whitelist)
      let selectedNames = new Set<string>();
      try {
        const agentRes = await api.get<{
          data?: { selectedSkills: string[] };
          selectedSkills?: string[];
        }>(`/skills/agents/${agentId}/skills?machineId=${machineId}`);
        const agentData = (agentRes as any)?.data || agentRes;
        // selectedSkills can be array of objects [{name, ...}] or strings
        const rawSelected = agentData.selectedSkills || agentData.selected || [];
        selectedNames = new Set(rawSelected.map((s: any) => typeof s === 'string' ? s : s.name));
      } catch {
        // If endpoint fails, assume all installed = enabled
        selectedNames = new Set(availableSkills.filter((s: any) => s.installed).map((s: any) => s.name));
      }

      // 3. Merge: only show installed skills, with enabled flag from whitelist
      const merged: SkillItem[] = availableSkills
        .filter((s: any) => s.installed)
        .map((s: any) => ({
          name: s.name,
          description: s.description || 'Skill instalada',
 emoji: s.emoji || '',
          category: s.category || 'general',
          version: s.version || '1.0.0',
          author: s.author || '',
          tags: s.tags || [],
          installed: true,
          enabled: selectedNames.has(s.name),
        }));

      setSkills(merged);
      setDirty(false);
      setSyncInfo({ source: 'API', time: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) });
      // Sync skills to D1
      const mid = cloudMachineId || runnerId;
      if (mid) {
        try {
          const skillNames = merged.filter((s: any) => s.enabled).map((s: any) => s.name);
          await api.put(`/cloud/machines/${mid}/agent-info`, {
            skills: JSON.stringify(skillNames),
          });
          console.log('[Skills] Synced to D1:', skillNames.length, 'skills');
        } catch (e) { console.warn('[Skills] D1 sync failed:', e); }
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar skills');
    } finally {
      setLoading(false);
    }
  }, [machineId, agentId, isOnline]);

  // loadSkillsFallback removed — all loading via API only

  useEffect(() => {
    loadSkills();
  }, [loadSkills]);

  // ─── Download Skills functions ──────────────────────────────────────
  async function loadAllSkills() {
    setLoadingAll(true);
    try {
      const { api } = await import('../api/client.js');
      const res = await api.get<any>('/cloud/all-skills');
      const data = (res as any)?.data || res;
      setAllSkills(data.skills || data || []);
    } catch (e: any) {
      console.error('[Skills] Failed to load all skills:', e);
    } finally {
      setLoadingAll(false);
    }
  }

  async function loadPendingInstalls() {
    try {
      const { api } = await import('../api/client.js');
      const res = await api.get<any>(`/skill-install/requests?machine_id=${installMachineId}`);
      const data = (res as any)?.data || res;
      const reqs = data.requests || [];
      setPendingInstalls(reqs.filter((r: any) => r.status === 'pending' || r.status === 'processing'));
    } catch { /* ignore */ }
  }

  useEffect(() => {
    if (installMachineId) loadPendingInstalls();
  }, [installMachineId]);

  async function handleOpenDownload() {
    setShowDownload(true);
    setSelectedSkills(new Set());
    setSearchQuery('');
    setSelectedCategory('all');
    if (allSkills.length === 0) await loadAllSkills();
  }

  async function handleInstallSkills() {
    if (selectedSkills.size === 0) return;
    setInstalling(true);
    try {
      const { api } = await import('../api/client.js');
      const skillsToInstall = Array.from(selectedSkills).map(name => {
        const s = allSkills.find(sk => sk.name === name);
        return { id: s?.id || name, name };
      });
      await api.post('/skill-install/request', {
        machine_id: installMachineId,
        runner_id: runnerId,
        skills: skillsToInstall,
      });
 setSaveResult({ type: 'success', message: ` ${skillsToInstall.length} skill(s) adicionada(s) à fila de instalação!` });
      setSelectedSkills(new Set());
      setShowDownload(false);
      loadPendingInstalls();
    } catch (e: any) {
 setSaveResult({ type: 'error', message: `Erro: ${e.message}` });
    } finally {
      setInstalling(false);
    }
  }

  const installedNames = new Set(skills.map(s => s.name));
  const categories = ['all', ...Array.from(new Set(allSkills.map(s => s.category || 'general').filter(Boolean)))];
  const filteredAllSkills = allSkills.filter(s => {
    if (selectedCategory !== 'all' && (s.category || 'general') !== selectedCategory) return false;
    if (searchQuery && !s.name.toLowerCase().includes(searchQuery.toLowerCase()) && !s.description?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  function toggleSkill(skillName: string) {
    setSkills(prev => prev.map(s =>
      s.name === skillName ? { ...s, enabled: !s.enabled } : s
    ));
    setDirty(true);
  }

  function toggleAll(enable: boolean) {
    setSkills(prev => prev.map(s => ({ ...s, enabled: enable })));
    setDirty(true);
  }

  async function handleSave() {
    setSaving(true);
    setSaveResult(null);
    try {
      const { api } = await import('../api/client.js');
      const enabledSkills = skills.filter(s => s.enabled).map(s => s.name);

      await api.put(`/skills/agents/${agentId}/skills?machineId=${machineId}`, {
        skills: enabledSkills,
      });

 setSaveResult({ type: 'success', message: `Whitelist salva! ${enabledSkills.length} skill${enabledSkills.length !== 1 ? 's' : ''} habilitada${enabledSkills.length !== 1 ? 's' : ''}.` });
      setDirty(false);
    } catch (err: any) {
 setSaveResult({ type: 'error', message: `Erro ao salvar: ${err.message || 'Falha desconhecida'}` });
    } finally {
      setSaving(false);
    }
  }

  const enabledCount = skills.filter(s => s.enabled).length;

  if (!isOnline) {
    return (
      <div style={{
        padding: 24, marginBottom: 20, borderRadius: 14,
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        textAlign: 'center', color: 'var(--text-muted)',
      }}>
 <span style={{ fontSize: 32 }}></span>
        <p style={{ margin: '8px 0 0' }}>Máquina offline. Conecte para ver skills.</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, position: 'relative' }}>
      <LockOverlay locked={locked} />
      {/* Unsaved changes indicator */}
      {dirty && (
        <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--border)' }}>
          <span style={{
            fontSize: 10, padding: '3px 8px', borderRadius: 4,
            background: T.warningBg, color: T.warning, fontWeight: 600,
          }}>● Alterações não salvas</span>
        </div>
      )}

      {/* Toast */}
      {saveResult && (
        <div style={{
          padding: '10px 16px', fontSize: 12, fontWeight: 600,
          background: saveResult.type === 'success' ? T.successBg : T.dangerBg,
          borderBottom: `1px solid ${saveResult.type === 'success' ? T.successBg : T.dangerBg}`,
          color: saveResult.type === 'success' ? T.success : T.danger,
        }}>
          {saveResult.message}
        </div>
      )}

      {/* Pending installs banner */}
      {pendingInstalls.length > 0 && (
        <div style={{
          padding: '10px 16px', fontSize: 12, fontWeight: 600,
          background: T.primaryBg, borderBottom: `1px solid ${T.primaryBgStrong}`,
          color: T.primary, display: 'flex', alignItems: 'center', gap: 8,
        }}>
 <span style={{ animation: 'pulse 2s infinite' }}></span>
          {pendingInstalls.length} skill(s) na fila de instalação...
          <Button variant="ghost" onClick={loadPendingInstalls} style={{
            background: 'none', border: 'none', color: T.primary, cursor: 'pointer', fontSize: 11, textDecoration: 'underline', marginLeft: 'auto',
          }}>Atualizar</Button>
        </div>
      )}

      {/* Baixar Skills button */}
      <div style={{ padding: '12px 20px 0' }}>
        <Button variant="ghost"
          onClick={handleOpenDownload}
          disabled={!isOnline}
          style={{
            width: '100%', padding: '10px 16px', borderRadius: 8,
            border: '1px dashed rgba(96,165,250,0.4)',
            background: showDownload ? T.primaryBg : T.primaryBg,
            color: T.primary, fontSize: 13, fontWeight: 600,
            cursor: isOnline ? 'pointer' : 'not-allowed',
            transition: 'all 0.15s',
            opacity: isOnline ? 1 : 0.5,
          }}
        >
 Baixar Skills
        </Button>
      </div>

      {/* Download Skills Panel */}
      {showDownload && (
        <div style={{
          margin: '0 20px', padding: 16, borderRadius: 10,
          background: T.primaryBg, border: `1px solid ${T.primaryBg}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
 <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>Skills Disponíveis</span>
            <Button variant="ghost" onClick={() => setShowDownload(false)} style={{
              background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16,
            }}></Button>
          </div>

          {/* Search + Category filter */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <input
              type="text"
              placeholder="Buscar skills..."
              value={searchQuery}
              onInput={(e: any) => setSearchQuery(e.target.value)}
              style={{
                flex: 1, padding: '8px 12px', borderRadius: 6, fontSize: 12,
                background: T.border, border: '1px solid var(--border)',
                color: 'var(--text)', outline: 'none',
              }}
            />
            <select
              value={selectedCategory}
              onChange={(e: any) => setSelectedCategory(e.target.value)}
              style={{
                padding: '8px 12px', borderRadius: 6, fontSize: 12,
                background: T.border, border: '1px solid var(--border)',
                color: 'var(--text)',
              }}
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat === 'all' ? 'Todas categorias' : cat}</option>
              ))}
            </select>
          </div>

          {loadingAll ? (
            <div style={{ padding: 16 }}><SkeletonBar width="50%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="30%" height={14} /></div>
          ) : (
            <>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>{filteredAllSkills.length} skills · {selectedSkills.size} selecionada(s)</span>
                <Button variant="ghost"
                  onClick={() => {
                    const next = new Set(selectedSkills);
                    for (const s of allSkills) {
                      const m = enrichSkill(s.name, skillsMeta);
                      if ((m.tier === 'tier1' || m.badge === 'verified') && !installedNames.has(s.name)) next.add(s.name);
                    }
                    setSelectedSkills(next);
                  }}
                  style={{
                    background: T.successBg, border: `1px solid ${T.successBgStrong}`,
                    color: T.success, fontSize: 10, fontWeight: 700, padding: '4px 10px',
                    borderRadius: 6, cursor: 'pointer', whiteSpace: 'nowrap',
                  }}
 >Instalar Todas Verificadas</Button>
              </div>
              <div style={{ maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {[...filteredAllSkills].sort((a, b) => {
                  const tierOrd = (name: string) => { const m = enrichSkill(name, skillsMeta); return m.tier === 'tier1' ? 0 : m.tier === 'tier2' ? 1 : 2; };
                  return tierOrd(a.name) - tierOrd(b.name);
                }).map(skill => {
                  const isInstalled = installedNames.has(skill.name);
                  const isSelected = selectedSkills.has(skill.name);
                  const skillMeta = enrichSkill(skill.name, skillsMeta);
                  const isTier1 = skillMeta.tier === 'tier1' || skillMeta.badge === 'verified';
                  return (
                    <label
                      key={skill.name}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 12px', borderRadius: 8, cursor: isInstalled ? 'not-allowed' : 'pointer',
                        background: isInstalled ? 'rgba(107,114,128,0.05)' : isTier1 ? T.successBg : isSelected ? T.primaryBg : 'transparent',
                        border: isTier1 ? `1px solid ${T.successBgStrong}` : isSelected ? `1px solid ${T.primaryBgStrong}` : '1px solid transparent',
                        opacity: isInstalled ? 0.5 : 1,
                        transition: 'all 0.1s',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isInstalled}
                        onChange={() => {
                          const next = new Set(selectedSkills);
                          if (next.has(skill.name)) next.delete(skill.name);
                          else next.add(skill.name);
                          setSelectedSkills(next);
                        }}
                        style={{ accentColor: T.primary }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          {skill.name}
                          {(() => { const m = enrichSkill(skill.name, skillsMeta); return <SkillBadge tier={m.tier} verified={m.verified} badge={m.badge} size="sm" />; })()}
                          {isInstalled && <span style={{ fontSize: 10, color: T.success, marginLeft: 2 }}>Instalada</span>}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {enrichSkill(skill.name, skillsMeta).description_pt || skill.description || 'Sem descrição'}
                        </div>
                      </div>
                      <span style={{ fontSize: 10, color: 'var(--text-muted)', flexShrink: 0 }}>{skill.category || 'general'}</span>
                    </label>
                  );
                })}
              </div>

              {/* Install button */}
              <Button variant="ghost"
                onClick={handleInstallSkills}
                disabled={installing || selectedSkills.size === 0}
                style={{
                  width: '100%', marginTop: 12, padding: '10px 16px', borderRadius: 8, border: 'none',
                  background: selectedSkills.size > 0 ? T.primary : T.borderHover,
                  color: selectedSkills.size > 0 ? 'white' : 'var(--text-muted)',
                  fontSize: 13, fontWeight: 600,
                  cursor: installing || selectedSkills.size === 0 ? 'not-allowed' : 'pointer',
                  opacity: installing ? 0.6 : 1,
                }}
              >
                {installing ? 'Enviando...' : selectedSkills.size > 0 ? `Instalar ${selectedSkills.size} skill(s)` : 'Selecione skills para instalar'}
              </Button>
            </>
          )}
        </div>
      )}

      <div style={{ padding: 20 }}>
        {loading && (
          <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: 24 }}>
            <div style={{ padding: 8 }}><SkeletonBar width="50%" height={14} /></div>
          </div>
        )}

        {error && (
          <div style={{
            padding: 12, borderRadius: 10,
            background: T.dangerBg, border: `1px solid ${T.dangerBgStrong}`,
            color: T.danger, fontSize: 12,
          }}>
            {error}
          </div>
        )}

        {!loading && !error && skills.length === 0 && (
          <div style={{
            padding: 24, borderRadius: 10,
            background: 'rgba(107,114,128,0.08)', border: '1px solid rgba(107,114,128,0.2)',
            color: 'var(--text-muted)', fontSize: 12, textAlign: 'center',
          }}>
            Nenhuma skill instalada nesta máquina.
            <br />
            <span style={{ fontSize: 11, opacity: 0.7 }}>
              Instale skills na aba Dashboard → Skills Disponíveis
            </span>
          </div>
        )}

        {!loading && !error && skills.length > 0 && (
          <>
            {/* Summary bar */}
            <div style={{
              padding: '10px 16px', borderRadius: 10, marginBottom: 16,
              background: T.successBg, border: `1px solid ${T.successBg}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12,
            }}>
              <span>
                <strong style={{ color: T.success }}>{enabledCount}</strong>
                <span style={{ color: 'var(--text-muted)' }}> de {skills.length} skill{skills.length !== 1 ? 's' : ''} habilitada{enabledCount !== 1 ? 's' : ''}</span>
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="ghost" onClick={() => toggleAll(true)} style={{
                  background: 'none', border: 'none', color: T.success,
                  fontSize: 11, cursor: 'pointer', textDecoration: 'underline', padding: 0,
                }}>Habilitar todas</Button>
                <Button variant="ghost" onClick={() => toggleAll(false)} style={{
                  background: 'none', border: 'none', color: 'var(--text-muted)',
                  fontSize: 11, cursor: 'pointer', textDecoration: 'underline', padding: 0,
                }}>Desabilitar todas</Button>
              </div>
            </div>

            {/* Skills list — grouped by tier sections */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {(() => {
                const groups: { verified: SkillItem[]; recommended: SkillItem[]; other: SkillItem[] } = { verified: [], recommended: [], other: [] };
                for (const s of skills) {
                  const m = enrichSkill(s.name, skillsMeta);
                  if (m.tier === 'tier1' || m.badge === 'verified') groups.verified.push(s);
                  else if (m.tier === 'tier2' || m.badge === 'recommended') groups.recommended.push(s);
                  else groups.other.push(s);
                }
                const sections = [
 { key: 'v', label: 'Verificadas', items: groups.verified, color: T.success },
 { key: 'r', label: 'Recomendadas', items: groups.recommended, color: T.warning },
 { key: 'o', label: 'Outras', items: groups.other, color: 'var(--text-muted)' },
                ];
                return sections.filter(sec => sec.items.length > 0).map(sec => (
                  <div key={sec.key}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: sec.color, padding: '10px 0 6px', borderBottom: '1px solid var(--border)', marginBottom: 6 }}>
                      {sec.label} ({sec.items.length})
                    </div>
                    {sec.items.map(skill => {
                      const meta = enrichSkill(skill.name, skillsMeta);
                      return (
                <div
                  key={skill.name}
                  onClick={() => toggleSkill(skill.name)}
                  style={{
                    padding: '12px 16px', borderRadius: 10, cursor: 'pointer',
                    background: skill.enabled ? T.successBg : T.bgSubtle,
                    border: skill.enabled ? `1px solid ${T.successBgStrong}` : '1px solid var(--border)',
                    display: 'flex', alignItems: 'center', gap: 12,
                    transition: 'all 0.15s',
                  }}
                >
                  {/* Toggle */}
                  <div style={{
                    width: 36, height: 20, borderRadius: 10, position: 'relative',
                    background: skill.enabled ? T.success : T.borderHover,
                    transition: 'background 0.2s', flexShrink: 0,
                  }}>
                    <div style={{
                      width: 16, height: 16, borderRadius: '50%',
                      background: 'white', position: 'absolute', top: 2,
                      left: skill.enabled ? 18 : 2,
                      transition: 'left 0.2s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    }} />
                  </div>

                  {/* Emoji */}
 <div style={{ width: 24, height: 24, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>{meta.emoji || skill.emoji || ''}</div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
                        {skill.name}
                      </span>
                      <SkillBadge tier={meta.tier} verified={meta.verified} badge={meta.badge} size="sm" />
                      {skill.version && (
                        <span style={{ fontSize: 10, color: 'var(--text-muted)', opacity: 0.6 }}>
                          v{skill.version}
                        </span>
                      )}
                    </div>
                    <div style={{
                      fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.4,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {meta.description_pt || skill.description}
                    </div>
                    <ApiKeyIndicator requiresApiKey={!!meta.requires_api_key} requiredKeys={meta.required_keys} size="sm" />
                  </div>

                  {/* Status badge */}
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 4,
                    flexShrink: 0,
                    background: skill.enabled ? T.successBg : T.bgActive,
                    color: skill.enabled ? T.success : 'rgba(156,163,175,1)',
                  }}>
                    {skill.enabled ? 'Habilitado' : 'Desabilitado'}
                  </span>
                </div>
                      );
                    })}
                  </div>
                ));
              })()}
            </div>

            {/* Save button */}
            <Button variant="ghost"
              onClick={handleSave}
              disabled={saving || !dirty}
              style={{
                width: '100%', marginTop: 16, padding: '12px 16px',
                borderRadius: 8, border: 'none',
                background: dirty
                  ? T.bgActive
                  : T.borderHover,
                color: dirty ? 'white' : 'var(--text-muted)',
                fontSize: 13, fontWeight: 600,
                cursor: saving || !dirty ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s',
                opacity: saving ? 0.6 : 1,
              }}
            >
              {saving ? 'Salvando...' : dirty ? `Salvar Whitelist (${enabledCount} skill${enabledCount !== 1 ? 's' : ''})` : 'Whitelist salva'}
            </Button>
          </>
        )}

        {/* Info footer */}
        {isAdmin.value && (<>
          <div style={{
            padding: '12px 16px', borderRadius: 10, marginTop: 16,
            background: T.bgSubtle, border: '1px solid var(--border)',
            fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.6,
          }}>
            <strong>Skills habilitadas</strong> ficam disponíveis para o agente usar.
            A whitelist é salva em <code style={{ color: 'var(--text)', fontFamily: 'monospace' }}>~/.openclaw/openclaw.json</code>.
          </div>
          {syncInfo && (
            <div style={{ padding: '6px 0', fontSize: 10, color: T.successBg, opacity: 0.7, marginTop: 8 }}>
              Sincronizado via {syncInfo.source} · /skills · {syncInfo.time}
            </div>
          )}
        </>)}
      {/* Source paths footer */}
      {isAdmin.value && (
        <div style={{
          marginTop: 20, padding: '10px 14px', borderRadius: 8,
          background: T.bgSubtle, border: '1px solid var(--border)',
          fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.8,
        }}>
          <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 11 }}>Fonte dos dados</div>
          <div><code style={{ background: T.bgCardHover, padding: '1px 4px', borderRadius: 3 }}>openclaw.json → skills.allowBundled</code> — Whitelist de skills habilitadas</div>
          <div><code style={{ background: T.bgCardHover, padding: '1px 4px', borderRadius: 3 }}>openclaw.json → skills.entries</code> — Config individual (enabled, apiKey, env)</div>
          <div><code style={{ background: T.bgCardHover, padding: '1px 4px', borderRadius: 3 }}>/usr/local/lib/node_modules/openclaw/skills/</code> — Skills bundled (sistema)</div>
          <div><code style={{ background: T.bgCardHover, padding: '1px 4px', borderRadius: 3 }}>workspace/skills/</code> — Skills instaladas pelo usuário (clawhub)</div>
          <div style={{ marginTop: 4, opacity: 0.7 }}>Lista via API · Habilitar/desabilitar via SSH (openclaw.json)</div>
        </div>
      )}
      </div>
    </div>
  );
}
